// Card search functionality for PeoplePage
// Searches through employee names only
// Respects active employee filters when searching

document.addEventListener("DOMContentLoaded", function () {
	const searchInputs = document.querySelectorAll(".js-card-search");

	// Cache filter elements once (shared across all search inputs)
	// Support both PeoplePage filters (.js-card-tag-filter) and ArticleList filters (#kompetanse)
	let cachedFilterSelect = null;
	let cachedCompetencySelect = null;
	let filterElementsCached = false;

	const getFilterElements = () => {
		if (filterElementsCached) {
			return {
				filterSelect: cachedFilterSelect,
				competencySelect: cachedCompetencySelect,
			};
		}

		// Try PeoplePage filters first
		cachedFilterSelect = document.querySelector(".js-card-tag-filter");
		cachedCompetencySelect = document.querySelector(
			".js-card-competency-filter"
		);

		// If not found, try ArticleList filters
		if (!cachedCompetencySelect) {
			cachedCompetencySelect = document.getElementById("kompetanse");
		}

		filterElementsCached = true;
		return {
			filterSelect: cachedFilterSelect,
			competencySelect: cachedCompetencySelect,
		};
	};

	// Shared filter change handler system (prevents duplicate listeners)
	// Each search input registers itself to be notified when filters change
	const filterChangeHandlers = new Set();
	let filterListenersAttached = false;

	const attachFilterListeners = () => {
		if (filterListenersAttached) return;

		const { filterSelect, competencySelect } = getFilterElements();
		const sharedHandler = () => {
			// Notify all registered search inputs
			filterChangeHandlers.forEach((handler) => handler());
		};

		if (filterSelect) {
			filterSelect.addEventListener("change", sharedHandler);
		}
		if (competencySelect) {
			competencySelect.addEventListener("change", sharedHandler);
		}

		filterListenersAttached = true;
	};

	// Pre-process card data once (shared across all search inputs)
	// This avoids repeated attribute parsing and string operations
	const cardDataCache = new WeakMap();
	const preprocessCardData = (card) => {
		if (cardDataCache.has(card)) {
			return cardDataCache.get(card);
		}

		const data = {
			// Cache primary filter data
			filterAttr: card.getAttribute("data-filter") || "",
			categoryAttr: card.getAttribute("data-category") || "",
			hasDataFilter: card.hasAttribute("data-filter"),
			// Pre-parse filter tags for PeoplePage (space-separated)
			filterArray: null,
			// Cache and pre-parse competency data (comma-separated)
			competencyAttr: card.getAttribute("data-competency") || "",
			competencyArray: null,
		};

		// Pre-parse filter tags for PeoplePage
		if (data.filterAttr) {
			data.filterArray = data.filterAttr
				.split(/\s+/)
				.filter((f) => f.trim() !== "");
		}

		// Pre-parse competency array (normalize to lowercase once)
		if (data.competencyAttr) {
			data.competencyArray = data.competencyAttr
				.split(",")
				.map((c) => c.trim())
				.filter((c) => c !== "")
				.map((c) => c.toLowerCase());
		}

		cardDataCache.set(card, data);
		return data;
	};

	// Shared filter checking logic (DRY - single source of truth)
	// Support both PeoplePage (data-filter) and ArticleList (data-category)
	const isCardVisibleByFilter = (card, selectedFilter, selectedCompetency) => {
		const cardData = preprocessCardData(card);

		// Check tag/category filter
		// PeoplePage uses "Alle", ArticleList uses "alle"
		let showByTag = true;
		if (
			selectedFilter &&
			selectedFilter !== "Alle" &&
			selectedFilter !== "alle"
		) {
			if (cardData.hasDataFilter) {
				// PeoplePage: use pre-parsed filterArray
				showByTag =
					cardData.filterArray && cardData.filterArray.includes(selectedFilter);
			} else if (cardData.categoryAttr) {
				// ArticleList: data-category is a single value
				showByTag = cardData.categoryAttr === selectedFilter;
			} else {
				showByTag = false;
			}
		}

		// Check competency filter
		// Use pre-parsed, normalized array
		let showByCompetency = true;
		if (selectedCompetency && selectedCompetency !== "alle") {
			const normalizedCompetency = selectedCompetency.toLowerCase();
			if (cardData.competencyArray) {
				showByCompetency =
					cardData.competencyArray.includes(normalizedCompetency);
			} else {
				showByCompetency = false;
			}
		}

		return showByTag && showByCompetency;
	};

	searchInputs.forEach((searchInput) => {
		// Find the closest cards container (look in the same container or the next container)
		// Only target containers with js-card-filter hook to avoid affecting search page
		let cardsContainer = searchInput
			.closest(".container")
			?.querySelector(".js-card-filter");

		// If not found in same container, try next container sibling
		if (!cardsContainer) {
			const currentContainer = searchInput.closest(".container");
			const nextContainer = currentContainer?.nextElementSibling;
			cardsContainer = nextContainer?.querySelector(".js-card-filter");
		}

		if (!cardsContainer) return;

		const cards = Array.from(cardsContainer.querySelectorAll(".card"));

		// Detect language from URL (similar to server-side detection)
		const isEnglish = window.location.pathname.includes("/en/");

		// Get visible results count element
		const resultsCountElement = searchInput
			.closest(".search-input")
			?.querySelector(".js-search-results-count");

		// Create ARIA live region for screen reader announcements
		let liveRegion = document.getElementById("js-search-results-announcement");
		if (!liveRegion) {
			liveRegion = document.createElement("div");
			liveRegion.id = "js-search-results-announcement";
			liveRegion.setAttribute("aria-live", "polite");
			liveRegion.setAttribute("aria-atomic", "true");
			liveRegion.className = "sr-only";
			liveRegion.style.cssText =
				"position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border-width: 0;";
			searchInput.parentElement.appendChild(liveRegion);
			// Link input to live region
			const describedBy = searchInput.getAttribute("aria-describedby");
			if (describedBy) {
				searchInput.setAttribute(
					"aria-describedby",
					`${describedBy} js-search-results-announcement`
				);
			} else {
				searchInput.setAttribute(
					"aria-describedby",
					"js-search-results-announcement"
				);
			}
		}

		// Debounce function with instance-specific timer
		let debounceTimer;
		const debounce = (func, delay) => {
			return function (...args) {
				clearTimeout(debounceTimer);
				debounceTimer = setTimeout(() => func.apply(this, args), delay);
			};
		};

		// Store original visibility state before search
		let originalVisibility = new Map();

		// Cache filter state to avoid repeated DOM queries
		let cachedFilterState = null;
		let cachedFilterTimestamp = 0;
		const FILTER_CACHE_TTL = 50; // Cache for 50ms to handle rapid changes
		let cachedFilteredCount = null; // Cache the count of filtered cards

		// Calculate filtered count (cards matching current filter, before search)
		const calculateFilteredCount = (selectedFilter, selectedCompetency) => {
			let count = 0;
			cards.forEach((card) => {
				if (isCardVisibleByFilter(card, selectedFilter, selectedCompetency)) {
					count++;
				}
			});
			return count;
		};

		// Get current filter state (cached)
		const getCurrentFilterState = () => {
			const now = Date.now();
			if (cachedFilterState && now - cachedFilterTimestamp < FILTER_CACHE_TTL) {
				return cachedFilterState;
			}

			const { filterSelect, competencySelect } = getFilterElements();
			const selectedFilter = filterSelect ? filterSelect.value : "Alle";
			const selectedCompetency = competencySelect
				? competencySelect.value
				: "alle";

			const state = {
				selectedFilter: selectedFilter,
				selectedCompetency: selectedCompetency,
			};

			cachedFilterState = state;
			cachedFilterTimestamp = now;

			// Update cached filtered count when filter state changes
			cachedFilteredCount = calculateFilteredCount(
				selectedFilter,
				selectedCompetency
			);

			return state;
		};

		// Cache card text content to avoid expensive textContent calls
		// Support both .card__name (PeoplePage) and .card__title + .card__description (ArticleList)
		const cardTextCache = new Map();
		const getCardText = (card) => {
			if (!cardTextCache.has(card)) {
				// Try to find name element (PeoplePage) or title/description (ArticleList)
				const nameElement = card.querySelector(".card__name");
				const titleElement = card.querySelector(".card__title");
				const descriptionElement = card.querySelector(".card__description");

				let searchText = "";
				if (nameElement) {
					// PeoplePage: search in name
					searchText = nameElement.textContent.toLowerCase();
				} else if (titleElement) {
					// ArticleList: search in title and description
					searchText = titleElement.textContent.toLowerCase();
					if (descriptionElement) {
						searchText += " " + descriptionElement.textContent.toLowerCase();
					}
				}
				cardTextCache.set(card, searchText);
			}
			return cardTextCache.get(card);
		};

		// Shared search logic (filtering cards)
		const filterCards = (searchTerm) => {
			// On first search, store original states
			if (originalVisibility.size === 0) {
				cards.forEach((card) => {
					originalVisibility.set(card, {
						display: card.style.display || "",
						hideClass: card.classList.contains("hide"),
						showClass: card.classList.contains("show"),
					});
				});
			}

			// Get filter state once (cached)
			const filterState = getCurrentFilterState();
			const { selectedFilter, selectedCompetency } = filterState;

			let visibleCount = 0;

			cards.forEach((card) => {
				// Get cached text content from the card
				const cardText = getCardText(card);
				const matchesSearch = cardText.includes(searchTerm);
				const passesFilter = isCardVisibleByFilter(
					card,
					selectedFilter,
					selectedCompetency
				);

				// Only show if it matches search AND passes current filter
				if (matchesSearch && passesFilter) {
					// Show the card
					card.style.display = "";
					card.classList.remove("hide");
					card.classList.add("show");
					visibleCount++;
				} else {
					// Hide the card (either doesn't match search or doesn't pass filter)
					card.style.display = "none";
					card.classList.add("hide");
					card.classList.remove("show");
				}
			});

			return visibleCount;
		};

		// Restore visibility - re-applies current filter instead of original state
		const restoreVisibility = () => {
			// Clear search-related state
			originalVisibility.clear();
			liveRegion.textContent = "";
			cachedFilterState = null;
			cachedFilteredCount = null; // Reset filtered count cache

			// Hide results count
			if (resultsCountElement) {
				resultsCountElement.style.display = "none";
				resultsCountElement.textContent = "";
			}

			// Re-apply the current active filter by directly applying filter logic
			// (matching card-filter.js behavior - uses classes only, no display styles)
			const { filterSelect, competencySelect } = getFilterElements();

			if (filterSelect) {
				const selectedFilter = filterSelect.value;
				const selectedCompetency = competencySelect
					? competencySelect.value
					: "alle";

				// Apply both filters: show cards matching both filters, hide others
				cards.forEach((card) => {
					card.style.display = ""; // Clear inline display style first

					const shouldShow = isCardVisibleByFilter(
						card,
						selectedFilter,
						selectedCompetency
					);

					if (shouldShow) {
						card.classList.remove("hide");
						card.classList.add("show");
					} else {
						card.classList.add("hide");
						card.classList.remove("show");
					}
				});
			} else {
				// No active filter - show all cards
				cards.forEach((card) => {
					card.style.display = ""; // Clear inline display style
					card.classList.remove("hide");
					card.classList.add("show");
				});
			}
		};

		// Update visible results count display
		const updateResultsCount = (count, isEn) => {
			if (!resultsCountElement) return;

			if (count === 0) {
				resultsCountElement.textContent = isEn
					? "No results"
					: "Ingen resultater";
			} else {
				// Use cached filtered count (calculated when filter changes)
				// If cache is missing, get filter state (which will update the cache)
				let filteredCount = cachedFilteredCount;
				if (filteredCount === null) {
					const filterState = getCurrentFilterState();
					filteredCount = cachedFilteredCount; // Updated by getCurrentFilterState
				}

				if (isEn) {
					resultsCountElement.textContent = `Showing ${count} of ${filteredCount} employees`;
				} else {
					resultsCountElement.textContent = `Viser ${count} av ${filteredCount} ansatte`;
				}
			}
			resultsCountElement.style.display = "";
		};

		// Accessible announcement for screen readers
		// Debounced version for live announcements (delays announcement by 300ms)
		const debouncedAnnounce = debounce((count, term, isEn) => {
			let announcement = "";
			if (isEn) {
				if (count === 0) {
					announcement = `No employees found matching "${term}"`;
				} else if (count === 1) {
					announcement = `1 employee found matching "${term}"`;
				} else {
					announcement = `${count} employees found matching "${term}"`;
				}
			} else {
				if (count === 0) {
					announcement = `Ingen ansatte funnet som matcher "${term}"`;
				} else if (count === 1) {
					announcement = `1 ansatt funnet som matcher "${term}"`;
				} else {
					announcement = `${count} ansatte funnet som matcher "${term}"`;
				}
			}
			liveRegion.textContent = announcement;
			// Note: updateResultsCount is called separately to avoid double updates
		}, 300);

		// Handle search input with debounced announcements
		const handleSearchWithDebounce = (e) => {
			e.preventDefault();
			const searchTerm = searchInput.value.toLowerCase().trim();

			// If search is cleared, restore original visibility
			if (searchTerm === "") {
				restoreVisibility();
				return;
			}

			const visibleCount = filterCards(searchTerm);
			// Update results count immediately
			updateResultsCount(visibleCount, isEnglish);
			// Use debounced announcement (only for screen reader, not count)
			debouncedAnnounce(visibleCount, searchTerm, isEnglish);
		};

		// Handle form submit with immediate announcements
		const handleSearch = (e) => {
			e.preventDefault();
			const searchTerm = searchInput.value.toLowerCase().trim();

			// If search is cleared, restore original visibility
			if (searchTerm === "") {
				restoreVisibility();
				return;
			}

			const visibleCount = filterCards(searchTerm);

			// Immediate announcement for form submit
			let announcement = "";
			if (isEnglish) {
				if (visibleCount === 0) {
					announcement = `No employees found matching "${searchTerm}"`;
				} else if (visibleCount === 1) {
					announcement = `1 employee found matching "${searchTerm}"`;
				} else {
					announcement = `${visibleCount} employees found matching "${searchTerm}"`;
				}
			} else {
				if (visibleCount === 0) {
					announcement = `Ingen ansatte funnet som matcher "${searchTerm}"`;
				} else if (visibleCount === 1) {
					announcement = `1 ansatt funnet som matcher "${searchTerm}"`;
				} else {
					announcement = `${visibleCount} ansatte funnet som matcher "${searchTerm}"`;
				}
			}

			liveRegion.textContent = announcement;
			updateResultsCount(visibleCount, isEnglish);
		};

		// Listen for input events (as user types) - use immediate filtering but debounced announcements
		searchInput.addEventListener("input", handleSearchWithDebounce);

		// Listen for form submit (if inside a form) - use immediate handleSearch for submit
		const form = searchInput.closest("form");
		if (form) {
			form.addEventListener("submit", handleSearch);
		}

		// Re-run search when filters change (for both PeoplePage and ArticleList filters)
		const handleFilterChange = () => {
			// Invalidate filter cache (will be recalculated on next getCurrentFilterState call)
			cachedFilterState = null;
			cachedFilteredCount = null;
			// Use requestAnimationFrame for better performance than setTimeout
			requestAnimationFrame(() => {
				if (searchInput.value.trim() !== "") {
					handleSearchWithDebounce(new Event("input"));
				}
			});
		};

		// Register this search input's handler with the shared filter change system
		filterChangeHandlers.add(handleFilterChange);
		attachFilterListeners();
	});
});
